import React, { useEffect, useState, useCallback, useId } from 'react';
import { Box, Typography, CircularProgress, Paper, Tabs, Tab, Button, Grid, TextField } from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { LeadFormActions } from '../../../Services/_actions/index.js';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import DynamicFormFieldsList from '../DynamicFormFields/List.tsx';
import LoadingButton from '../../Shared/Loading/index.tsx';
import ChildCampaignLeads from './ChildCampaignLeads.tsx';
import { useRecoilState } from 'recoil';
import { campaignDataMapAtom } from '../../../Services/_states/index.js';
import { convertGroupFormIntoArr, groupLeadItems, normalizeDateFormat, validateDynamicForm, validateFormFieldValue } from '../DynamicFormFields/helper.ts';

interface FormField {
    item_id?: string;
    key_name?: string;
    alias_name?: string;
    key_type?: string;
    key_options?: string[];
    value?: any;
}

interface ChildCampaign {
    child_campaign_id: string;
    child_campaign_name: string;
}

interface CampaignState {
    campaignId: string;
    leadFormId?: string;
    campaignName: string;
    formFields: FormField[];
    childCampaigns: ChildCampaign[];
    isLoading: boolean;
    parentLead?: any;
}

const EditLeadForm: React.FC = () => {
    const { getFormsByCampaignId, fetchCampaignMappings, updateLeadV1 } = LeadFormActions();
    const { showSnackBar } = useSnackBar();
    const { campaignId: initialCampaignId, leadFormId: initialLeadFormId } = useParams();
    const { state } = useLocation();
    const navigate = useNavigate();
    const parentLead = state?.parentLead;
    // console.log("🚀 ~ file: index.tsx:20 ~ EditLeadForm ~ parentLead:", parentLead)
    const [campaignDataMap, setCampaignDataMap] = useRecoilState<{ [key: string]: CampaignState }>(campaignDataMapAtom);
    const [selectedPath, setSelectedPath] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState<any>(null);
    const [errors, setErrors] = useState<any>('');
    const [fieldErrors, setFieldErrors] = useState<{ [key: number]: string }>({});

    // function mergeLeadData(items: any[] = [], data: any[] = []): any[] {
    //     const overrideMap = new Map < number, any> (
    //         data.map(d => {
    //             const id = typeof d.item_id === 'string'
    //                 ? parseInt(d.item_id, 10)
    //                 : d.item_id;
    //             return [id, d.value] as [number, any];
    //         }).filter(([id]) => !isNaN(id))
    //     );
    //     return items.map(field => {
    //         const id = typeof field.item_id === 'string'
    //             ? parseInt(field.item_id, 10)
    //             : field.item_id;
    //         const hasOverride = overrideMap.has(id);
    //         const overrideValue = overrideMap.get(id);
    //         return {
    //             ...field,
    //             value: hasOverride
    //                 ? overrideValue
    //                 : field.value ?? ''
    //         };
    //     });
    // }

    function mergeLeadData(items: any[] = [], data: any[] = [], leadId?: string): any[] {
        const overrideMap = new Map<string, any>();

        if (Array.isArray(data)) {
            data.forEach(d => {
                // if (d?.item_id !== undefined) {
                //     overrideMap.set(String(d.item_id), d.value);
                if (d?.item_id !== undefined) {
                    let val = d.value;
                    if (typeof val === 'string' && d?.key_type === 'DATE') {
                        val = normalizeDateFormat(val);
                    }
                    overrideMap.set(String(d.item_id), val);
                } else if (d?.key_name) {
                    const match = items.find(i =>
                        i.alias_name === d.alias_name ||
                        i.key_name === d.key_name
                        // i.label_name === d.key_name ||
                        // i.field_name === d.key_name
                    );

                    if (match?.item_id && !overrideMap.has(String(match.item_id))) {
                        let val = d.value;
                        if (typeof val === 'string' && d?.key_type === 'DATE') {
                            val = normalizeDateFormat(val);
                        }
                        overrideMap.set(String(match.item_id), val);
                    }
                }

                // } else if (d?.key_name) {
                //     const match = items.find(i => i.key_name === d.key_name);
                //     if (match?.item_id && !overrideMap.has(String(match.item_id))) {
                //         overrideMap.set(String(match.item_id), d.value);
                //     }
                // }
            });
        }

        // 💡 Manually inject lead_id if still not set
        const leadField = items.find(f => f.key_name === 'lead_id' || f.alias_name === 'Lead_ID');
        if (leadField && leadId && !overrideMap.has(String(leadField.item_id))) {
            overrideMap.set(String(leadField.item_id), leadId);
        }

        console.log("Override Map:", overrideMap);

        return items.map(field => {
            const id = String(field.item_id);
            return {
                ...field,
                value: overrideMap.has(id) ? overrideMap.get(id) : (field.value ?? '')
            };
        });
    }



    const loadCampaign = useCallback(async (campaignId: string, parentLead: any = null) => {
        const cached: any = campaignDataMap[campaignId];
        if (cached) return;

        setCampaignDataMap(prev => ({
            ...prev,
            [campaignId]: { ...cached, isLoading: true } as CampaignState
        }));

        try {
            let formFields: FormField[] = [];
            let dynamicFormGroups: any[] = [];
            let campaignName = 'Unknown Campaign';
            let childCampaigns: ChildCampaign[] = [];
            let leadFormId: string = '';

            const formRes = await getFormsByCampaignId(campaignId);
            if (formRes?.status === 200) {
                const forms = Object.values(formRes.data || {});
                const target: any = forms.find((f: any) => f.leads_form_id === initialLeadFormId) || forms[0];
                if (target) {
                    const data: any = Object.values(target.leads_form_items || []);
                    if (campaignId === initialCampaignId) {
                        let leadFormData = mergeLeadData(data || [], parentLead?.data || [], parentLead?.lead_id);
                        // let leadFormData = mergeLeadData(data || [], parentLead?.data || []);
                        formFields = groupLeadItems(leadFormData, target?.group_options || []);
                    } else {
                        formFields = data || [];
                        dynamicFormGroups = target?.group_options || []
                    }
                    leadFormId = target?.leads_form_id || '';
                    campaignName = target.leads_form_name || campaignName;
                }
            }

            const mapRes = await fetchCampaignMappings(campaignId);
            if (mapRes?.status === 200) {
                childCampaigns = mapRes?.data?.child_mappings || [];
                if (campaignName === 'Unknown Campaign') campaignName = mapRes.data?.parent_campaign_name || campaignName;
            }

            setCampaignDataMap(prev => ({
                ...prev,
                [campaignId]: {
                    campaignId,
                    leadFormId,
                    campaignName,
                    parentLead,
                    formFields,
                    childCampaigns,
                    dynamicFormGroups,
                    isLoading: false
                }
            }));
        } catch (err) {
            console.log(err, "err")
            showSnackBar(`Failed to load data for ${campaignId}`, 'error');
            setCampaignDataMap(prev => ({
                ...prev,
                [campaignId]: { ...(prev[campaignId] || {}), isLoading: false }
            }));
        }
    }, [campaignDataMap, parentLead, initialLeadFormId]);

    useEffect(() => {
        if (!initialCampaignId || !initialLeadFormId || !parentLead?.id) {
            showSnackBar('Missing lead or campaign data', 'error');
            navigate(-1);
            return;
        }
        setSelectedPath([initialCampaignId]);
        loadCampaign(initialCampaignId, parentLead);
    }, [initialCampaignId, initialLeadFormId, parentLead]);

    const handleSelectRow = useCallback((row: any, campaignId?: string, level?: number) => {
        if (campaignId) {
            setCampaignDataMap((prev) => ({
                ...prev,
                [campaignId]: { ...prev[campaignId], parentLead: row }
            }));
            if (typeof level === 'number') {
                const newPath = [...selectedPath.slice(0, level + 1), campaignId];
                setSelectedPath(newPath);
                loadCampaign(campaignId, row);
            }
        }
    }, [selectedPath, loadCampaign, setCampaignDataMap]);

    const handleTabChange = useCallback((level: number, newValue: string, parentLead = null) => {
        const newPath = [...selectedPath.slice(0, level + 1), newValue];
        setSelectedPath(newPath);
        loadCampaign(newValue, parentLead);
        if (newValue) {
            setCampaignDataMap((prev) => ({
                ...prev,
                [newValue]: { ...prev[newValue], parentLead: parentLead }
            }));
        }
    }, [selectedPath]);
    // }, [selectedPath, loadCampaign]);

    // const handleSelectRow = useCallback((row: any, campaignId?: string,level:number) => {
    //     if (campaignId) {
    //         setCampaignDataMap((prev) => ({
    //             ...prev,
    //             [campaignId]: { ...prev[campaignId], parentLead: row }
    //         }));
    //         const newPath = [...selectedPath.slice(0, level + 1), campaignId];
    //         setSelectedPath(newPath);
    //         loadCampaign(campaignId,row)
    //         }
    // }, []);

    const handleFieldChange = (index: number, value: any, groupTitle: string) => {
        const cid = initialCampaignId!;
        try {
            setCampaignDataMap(prev => {
                const updated = { ...prev[cid] };
                const updatedFormFields = { ...updated.formFields };
                const groupForm = [...updatedFormFields[groupTitle]];
                groupForm[index] = { ...groupForm[index], value: value };
                updatedFormFields[groupTitle] = [...groupForm];
                updated.formFields = { ...updatedFormFields };
                return { ...prev, [cid]: updated };
            });
            const updated = { ...campaignDataMap[cid] };
            const updatedFormFields = { ...updated.formFields };
            const groupForm = [...updatedFormFields[groupTitle]];
            groupForm[index] = { ...groupForm[index], value: value };
            updatedFormFields[groupTitle] = [...groupForm];
            updated.formFields = { ...updatedFormFields };
            setCampaignDataMap({ ...campaignDataMap, [cid]: updated });
            const errorMsg = validateFormFieldValue(groupForm[index], value);
            if (errorMsg) {
                setFieldErrors((prev) => ({ ...prev, [`${groupTitle}_${index}`]: errorMsg }));
            } else {
                setFieldErrors((prev) => {
                    const { [`${groupTitle}_${index}`]: _, ...rest } = prev;
                    return { ...rest };
                });
            }

        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (parentLead?.phone_number) {
            setPhoneNumber(parentLead?.phone_number || '')
        }
    }, [parentLead])


    const handleSave = async () => {
        try {
            if (!parentLead?.id || !initialCampaignId) {
                showSnackBar("Lead not updated successfully", "error");
                return;
            }
            let payload: any = {
                area: parentLead.area || '',
                city: parentLead.city || '',
                lead_id: parentLead.lead_id || '',
                leads_form_entries: [],
                phone_number: parentLead.phone_number || '',
                supplier_id: parentLead.supplier_id || '',
                supplier_name: parentLead.supplier_name || '',
            };

            if (!campaignDataMap[initialCampaignId]?.formFields) {
                showSnackBar("Form cannot be submitted without entries.", "error");
                return;
            }
            setIsSaving(true);
            const fields = campaignDataMap[initialCampaignId]?.formFields;
            const { fieldErrors, hasErrors } = validateDynamicForm(campaignDataMap[initialCampaignId]?.formFields);
            if (hasErrors) {
                setFieldErrors(fieldErrors);
                showSnackBar("Please fill all required fields", "error");
                return;
            } else {
                setFieldErrors({});
            }
            const newArr: any = convertGroupFormIntoArr(fields);
            let entryList = newArr?.map((item) => {
                if (item?.key_type === "PHONE") {
                    payload.phone_number = item.value ?? '';
                }
                return {
                    item_id: item.item_id,
                    key_name: item?.key_name,
                    alias_name: item?.alias_name,
                    value: item?.value ?? "",
                };
            });
            payload.leads_form_entries = entryList;
            const res: any = await updateLeadV1(payload);
            if (res?.status === 200) {
                showSnackBar("Form saved successfully!", "success");
            } else {
                showSnackBar("Failed to save form. Please try again.", "error");
                console.error("Save error response:", res);
            }
        } catch (error: any) {
            showSnackBar("An unexpected error occurred. Please check console logs.", "error");
        } finally {
            setIsSaving(false);
        }
    };

    const renderTabs = (campaignId: string, level: number): React.ReactNode => {
        const campaignData = campaignDataMap[campaignId];
        if (!campaignData) return null;

        let selectedChildId = selectedPath[level + 1];

        // 🔁 Auto-select first child campaign tab if not yet selected
        if (
            !selectedChildId &&
            campaignData?.childCampaigns?.length > 0 &&
            campaignData?.parentLead
        ) {
            selectedChildId = campaignData.childCampaigns[0].child_campaign_id;
            setTimeout(() => {
                handleTabChange(level, selectedChildId, null);
            }, 0);
        }
        try {
            return (
                <Box
                    sx={{
                        pl: level > 0 ? 0 : 0,
                        mb: 1,
                        borderLeft: level > 0 ? '2px solid #eee' : '2px solid #eee',
                    }}
                >
                    {initialCampaignId === campaignData?.campaignId && (
                        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>{campaignData?.campaignName || ''}</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        label={
                                            <Typography component="span">
                                                Phone Number <span style={{ color: 'red' }}>*</span>
                                            </Typography>
                                        }
                                        placeholder="Enter Mobile Number"
                                        variant="outlined"
                                        size="small"
                                        value={phoneNumber || ''}
                                        disabled={!!(parentLead?.lead_id && parentLead?.phone_number)}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/\D/g, '');
                                            let mobileError = "";
                                            if (/[^0-9]/.test(value)) {
                                                mobileError = "Characters are not allowed";
                                            }
                                            setErrors(mobileError);
                                            if (!mobileError) {
                                                setPhoneNumber(value);
                                                setErrors("");
                                            }
                                        }}
                                        error={!!errors}
                                        helperText={errors || ''}
                                        inputProps={{
                                            maxLength: 13,
                                            inputMode: "numeric",
                                            pattern: "[0-9]*"
                                        }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: '#000000',
                                                color: '#000000',
                                                opacity: 1,
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: '#000000',
                                                opacity: 1,
                                            },
                                        }}

                                    />
                                </Grid>
                            </Grid>
                            <DynamicFormFieldsList
                                data={campaignData?.formFields || []}
                                onChange={handleFieldChange}
                                fieldErrors={fieldErrors}
                            />
                            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                <Button variant="outlined" size="small" onClick={() => navigate(-1)}>Cancel</Button>
                                <LoadingButton
                                    variant="contained"
                                    onClickButton={handleSave}
                                    buttonName="Save Changes"
                                    size="small"
                                    isDisabled={isSaving}
                                    isLoading={isSaving}
                                />
                            </Box>
                        </Paper>
                    )}

                    {/* Render child campaigns if they exist and parentLead is present */}
                    {Array.isArray(campaignData.childCampaigns) && campaignData.childCampaigns.length > 0 && parentLead && (
                        <>
                            {campaignData?.parentLead &&
                                <Box sx={{ width: '100%', bgcolor: 'background.paper' }}>
                                    <Tabs
                                        value={selectedChildId ?? false}
                                        onChange={(e, newVal) => handleTabChange(level, newVal)}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        aria-label="Child Campaigns Tabs"
                                        sx={{
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                        }}
                                    >
                                        {campaignData?.childCampaigns?.map((child) => (
                                            <Tab
                                                key={child?.child_campaign_id + useId}
                                                label={`${child.child_campaign_name}-${child.child_campaign_id}`}
                                                value={child.child_campaign_id}
                                                aria-labelledby={`tab-${child.child_campaign_id}`}
                                                sx={{
                                                    '&.Mui-selected': {
                                                        color: 'primary.main', // Tab selected color
                                                        fontWeight: 'bold', // Bold selected tab
                                                    },
                                                    '&:not(.Mui-selected)': {
                                                        color: 'text.secondary', // Non-selected tab color
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Tabs>
                                </Box>
                            }
                            {selectedChildId && (
                                <>
                                    {campaignDataMap[selectedChildId]?.campaignId &&
                                        campaignDataMap[selectedChildId]?.leadFormId && (
                                            <ChildCampaignLeads
                                                campaignId={campaignDataMap[selectedChildId]?.campaignId}
                                                leadFormId={campaignDataMap[selectedChildId]?.leadFormId}
                                                dynamicFormFields={campaignDataMap[selectedChildId]?.formFields || []}
                                                dynamicFormGroups={campaignDataMap[selectedChildId]?.dynamicFormGroups || []}
                                                parentLead={campaignData?.parentLead}
                                                handleSelectRow={(row) => { handleTabChange(level, campaignDataMap[selectedChildId]?.campaignId, row) }}
                                                tabName={`${campaignDataMap[selectedChildId]?.campaignName || 'Form'} Dashboard`}
//${campaignDataMap[selectedChildId]?.campaignId}
          showSparePartsInfo={campaignDataMap[selectedChildId]?.campaignName?.includes("Spare Parts")}

                                            />
                                        )}
                                    {renderTabs(selectedChildId, level + 1)}
                                </>
                            )}
                        </>
                    )}
                </Box>
            );
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <Box sx={{ width: "100%" }}>

            <Typography variant="h5" align="left" fontWeight="bold" gutterBottom >Edit Lead (ID: {parentLead?.lead_id})</Typography>

 {/* <Box sx={{ display: 'flex', overflowX: 'auto', mb: 2, gap: 1 }}>
      {Object.values(campaignDataMap).map((data) => (
        <Button
          key={data.campaignId}
          variant={selectedPath[0] === data.campaignId ? 'contained' : 'outlined'}
          color="primary"
          size="small"
          onClick={() => {
            handleTabChange(0, data.campaignId, data.parentLead || parentLead);
          }}
        >
          {data.campaignName || 'Untitled'}
        </Button>
      ))}
    </Box> */}


            {initialCampaignId && renderTabs(initialCampaignId, 0)}
            {initialCampaignId && campaignDataMap[initialCampaignId]?.isLoading && <Box sx={{ textAlign: 'center', mt: 4 }}><CircularProgress /></Box>}
        </Box>
    );
};

export default EditLeadForm;
